import { create } from 'zustand';
import { useToasts } from '@/store/toasts';

/**
 * One-shot "pick a point on the map" mode. `start()` arms it with a sticky
 * toast; MapView's click handler consumes the NEXT map click (before any
 * mode logic) and calls `onPick`. Esc or a second `start` cancels. Used by
 * "Set RTL point" on the asset rows; any future point-picking flows should
 * ride this too.
 */
interface MapPickStore {
  active: { label: string; onPick: (lat: number, lon: number) => void } | null;
  toastId: string | null;
  start: (label: string, onPick: (lat: number, lon: number) => void) => void;
  /** Consume the armed pick. Returns the handler, or null if not armed. */
  take: () => ((lat: number, lon: number) => void) | null;
  cancel: () => void;
}

export const useMapPick = create<MapPickStore>((set, get) => ({
  active: null,
  toastId: null,

  start: (label, onPick) => {
    get().cancel();
    const toastId = useToasts.getState().push({
      tone: 'info',
      title: label,
      body: 'Click the map to place it — Esc cancels.',
      durationMs: 0,
    });
    set({ active: { label, onPick }, toastId });
  },

  take: () => {
    const { active, toastId } = get();
    if (!active) return null;
    if (toastId) useToasts.getState().dismiss(toastId);
    set({ active: null, toastId: null });
    return active.onPick;
  },

  cancel: () => {
    const { toastId } = get();
    if (toastId) useToasts.getState().dismiss(toastId);
    set({ active: null, toastId: null });
  },
}));
